import { LoadingButton } from "@mui/lab";
import { Stack, Typography, useTheme } from "@mui/material";

import { FormProvider, RHFTextField } from "@app/components";
import { useDialog, useRequestState, useResponsive } from "@app/hooks";
import { useForm } from "react-hook-form";
import { Strings } from "@app/constants";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { resetUserInfo, userInfo } from "../slice";
import { selectUserInfoState } from "../selectors";
import { IUserInfoFormValues } from "../types";
import { useCallback, useEffect } from "react";
import { selectMyProfileState } from "@app/modules/my-profile/selector";
import { BookingSuccessDialog } from "@app/components/modals/booking-success-dialog";
import { PATH_DASHBOARD } from "@app/routes/paths";

const userInfoFields = [
  [
    {
      name: "firstName",
      label: "First Name",
      placeholder: "Enter first name",
      type: "text",
    },
    {
      name: "lastName",
      label: "Last Name",
      placeholder: "Enter last name",
      type: "text",
    },
  ],
  [
    {
      name: "mobileNumber",
      label: "Mobile Number",
      placeholder: "Enter mobile number",
      type: "tel",
    },
    {
      name: "email",
      label: "Email",
      placeholder: "Enter email address",
      type: "email",
    },
  ],
];

const UserInfo = () => {
  const isMobile = useResponsive("down", "sm");
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const bookingSuccess = useDialog();
  const { visible, show, hide } = bookingSuccess;

  const { data: profileData } = useRequestState({
    stateSelector: selectMyProfileState,
  });

  const methods = useForm<IUserInfoFormValues>({
    defaultValues: {
      firstName: profileData?.firstName || "",
      lastName: profileData?.lastName || "",
      email: profileData?.email || "",
      mobileNumber: profileData?.phone || "",
    },
    mode: "onChange",
  });

  useEffect(() => {
    methods.reset({
      firstName: profileData?.firstName || "",
      lastName: profileData?.lastName || "",
      email: profileData?.email || "",
      mobileNumber: profileData?.phone || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData]);

  const { state } = useLocation();

  const onSuccess = useCallback(() => {
    show();
    dispatch(resetUserInfo());
  }, [dispatch]);

  const { loading } = useRequestState({
    stateSelector: selectUserInfoState,
    successMessageShown: true,
    errorShown: true,
    onSuccess,
  });

  const onSubmit = (formData: IUserInfoFormValues) => {
    dispatch(
      userInfo({
        ...formData,
        cardId: state?.carDetails?.id,
      }),
    );
  };

  return (
    <>
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
        <Stack
          spacing={4}
          sx={{
            p: { xs: 2, sm: 3, md: 5 },
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 3,
            backgroundColor: "#fff",
          }}
        >
          <Stack spacing={1} alignItems={"center"}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: theme.palette.grey[900],
              }}
            >
              {Strings.testDrive.bookYourTest}
            </Typography>
          </Stack>

          <Stack spacing={3}>
            {userInfoFields.map((group, rowIndex) => (
              <Stack
                key={rowIndex}
                direction={isMobile ? "column" : "row"}
                spacing={2}
                sx={{ width: "100%" }}
              >
                {group.map((field) => (
                  <Stack
                    key={field.name}
                    spacing={1}
                    flex={1}
                    sx={{ width: "100%" }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.grey[800],
                      }}
                    >
                      {field.label}
                    </Typography>

                    <RHFTextField
                      name={field.name}
                      placeholder={field.placeholder}
                      type={field.type}
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                          height: 52,
                          borderRadius: 2,
                          backgroundColor: theme.palette.grey[100],
                          "& fieldset": {
                            border: "none",
                          },
                          "&:hover": {
                            backgroundColor: theme.palette.grey[200],
                          },
                          "&.Mui-focused": {
                            backgroundColor: "#fff",
                            border: `1px solid ${theme.palette.primary.main}`,
                          },
                        },
                      }}
                    />
                  </Stack>
                ))}
              </Stack>
            ))}
          </Stack>

          <Stack direction="row" justifyContent={"center"}>
            <LoadingButton
              loading={loading}
              type="submit"
              variant="contained"
              sx={{
                px: 5,
                height: 48,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
              }}
            >
              Confirm Detail
            </LoadingButton>
          </Stack>
        </Stack>
      </FormProvider>

      <BookingSuccessDialog
        onClose={() => {
          hide();
          dispatch(resetUserInfo());
          navigate(PATH_DASHBOARD.home);
        }}
        open={visible}
      />
    </>
  );
};

export { UserInfo };
